/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';

import {AnalyticsBrowser} from '@segment/analytics-next';

import {config} from 'config/env';
import {useAppSelector} from 'hooks';
import ReactNative from 'services/react-native';
import {AnalyticsSource, COMMON_ATTRIBUTES, JSONObject} from 'types';
import {getDeviceType} from 'utils/analytics';

export const ANALYTICS = {
  UPDATE_BASKET_ITEM: 'updateBasketItem',
  EDIT_BASKET: 'editBasket',
  ADD_TO_BASKET: 'addToBasket',

  // BASKET DRAWER PIN-MAP AND ADDRESS EVENTS
  ADDRESS_LIST_OPEN: 'addressListOpen',
  ADDRESS_NEW_OPEN: 'addressNewOpen',
  ADDRESS_EDIT_OPEN: 'addressEditOpen',
  ADDRESS_DECODED: 'addressDecoded',
  ADDRESS_LOCATION_PROMPT: 'addressLocationPrompt',
  ADDRESS_LOCATION_CONFIRMED: 'addressLocationConfirmed',
  ADDRESS_CREATED: 'addressCreated',
  ADDRESS_EDITED: 'addressEdited',
  ADDRESS_ACCEPTED_LOCATION_MISMATCH: 'addressAcceptedLocationMismatch',

  PDP_CLICK: 'pdpClick',
  CAROUSEL_SCROLL: 'carouselScroll',
  CHECKOUT_START: 'checkoutStart',
  CAROUSEL_IMPRESSION: 'carouselImpression',

  BASKET_SUMMARY_OPEN: 'openBasketSummary',

  BASKET_MODE_SWITCHED: 'basketModeSwitched'
};

export const BASKET_PDP_ATTRIBUTES = {
  PAGE_NAME: 'basket',
  SOURCE_EVENT: 'basketItems',
  BASKET_RECOMMENDATION_V2: 'basketRecommendationCarouselv2',
  RECOMMEND_TYPE: 'PERSONALIZED',
  TRIGGER_TYPE_PRODUCT_CARD: 'productCard',
  TRIGGER_TYPE_QUANTITY_COUNT: 'quantityCount',
  TRIGGER_TYPE_PLUS_BUTTON: 'plusButton'
};

export const BASKET_UPDATE_ITEM_SOURCE_EVENTS = {
  INCREASE_ITEM: 'increaseItem',
  DECREASE_ITEM: 'decreaseItem',
  DELETE_ITEM: 'deleteItem'
};

export const BASKET_UPDATE_ITEM_TRIGGER_TYPE = {
  KEYBOARD: 'keyboard',
  BUTTON: 'button',
  BUTTON_HOLD: 'buttonHold'
};

export const BASKET_EDIT_SOURCE_EVENTS = {
  ADD_ADDRESS: 'addAddress',
  EDIT_ADDRESS: 'editAddress',
  SWITCH_ADDRESS: 'switchAddress',
  ADD_VOUCHER: 'addVoucher',
  REMOVE_VOUCHER: 'removeVoucher',
  APPLY_REDEEM_POINTS: 'applyRedeemPoints',
  CANCEL_REDEEM_POINTS: 'cancelRedeemPoints',
  OOS_FRICTION: 'oosFriction',
  PRODUCT_CAPPING: 'productCapping'
};

export const SHOPPING_EVENTS = {
  TRIGGER_TYPE: 'seeAllCard'
};

export const SUGGESTEDLIST_CHANNELS = {
  MKP_WEB: 'mkp_web',
  MKP_APP: 'mkp_app',
  MAKRO_PRO: 'MPRO'
};

export const EVENT_SOURCE = {
  WEB: 'web-mango-prod',
  APP: 'mango-prod'
};

export enum RecommendationDetailType {
  BASKET_RECOMMENDATION = 'basketRecommendation'
}

export enum RecommendationVariants {
  RECOMMENDED_FOR_YOU = 'RECOMMENDED_FOR_YOU',
  BASKET = 'BASKET'
}

export enum ScrollDirection {
  LEFT = 'left',
  RIGHT = 'right'
}

export enum CarouselSize {
  Normal = 'NORMAL',
  Compact = 'COMPACT'
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const AnalyticsContext = React.createContext<AnalyticsBrowser>(undefined!);

type Props = {
  children: React.ReactNode;
};

/**
 * Usage:
 * const analytics = useAnalytics()
 * analytics.trackEvent()
 */
export const AnalyticsContextProvider = ({children}: Props) => {
  const analytics = React.useMemo(
    () => AnalyticsBrowser.load({writeKey: config.SEGMENT_ANALYTICS_KEY}),
    [config.SEGMENT_ANALYTICS_KEY]
  );
  return <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>;
};

// Create an analytics hook that we can use with other components.
export const useAnalytics = () => {
  const analyticsContext = React.useContext(AnalyticsContext);
  const previousRoute = undefined;

  const {customerId} = useAppSelector((state) => state?.auth);

  if (!analyticsContext) {
    throw new Error('Context used outside of its Provider!');
  }

  // adding the common attributes to every event
  const addSourceField = (parameters: JSONObject): JSONObject => {
    if (!parameters?.source) {
      parameters[AnalyticsSource.KEY] = ReactNative.isReactNativeWebView()
        ? AnalyticsSource.VALUE_APP
        : AnalyticsSource.VALUE_WEB;
    }
    return parameters;
  };

  // Track a specific event
  const trackEvent = (eventName: string, parameters: JSONObject) => {
    analyticsContext.track(eventName, addCommonDefaultParams(parameters));
  };

  const addPageUrlAttribute = (parameters: JSONObject): JSONObject => {
    if (!parameters?.[COMMON_ATTRIBUTES.PAGE_URL]) {
      const pageUrl = window.location.pathname;
      parameters[COMMON_ATTRIBUTES.PAGE_URL] = pageUrl;
    }
    return parameters;
  };

  const addPageNameAttribute = (parameters: JSONObject): JSONObject => {
    if (!parameters?.[COMMON_ATTRIBUTES.PAGE_NAME]) {
      parameters[COMMON_ATTRIBUTES.PAGE_NAME] = 'stand-alone-basket';
    }
    return parameters;
  };

  const addDeviceTypeAttribute = (parameters: JSONObject): JSONObject => {
    if (!parameters?.[COMMON_ATTRIBUTES.DEVICE_TYPE]) {
      const deviceType = getDeviceType();
      parameters[COMMON_ATTRIBUTES.DEVICE_TYPE] = deviceType;
    }
    return parameters;
  };

  const addRefNameAttribute = (parameters: JSONObject): JSONObject => {
    if (!parameters?.[COMMON_ATTRIBUTES.REF_NAME]) {
      parameters[COMMON_ATTRIBUTES.REF_NAME] = previousRoute || '';
    }
    return parameters;
  };

  const addCommonDefaultParams = (parameters: JSONObject) => {
    parameters[COMMON_ATTRIBUTES.PLATFORM] = ReactNative.isReactNativeWebView()
      ? AnalyticsSource.VALUE_APP
      : AnalyticsSource.VALUE_WEB;
    parameters[COMMON_ATTRIBUTES.USER_ID] = customerId || '';
    addSourceField(parameters);
    addPageNameAttribute(parameters);
    addPageUrlAttribute(parameters);
    addDeviceTypeAttribute(parameters);
    addRefNameAttribute(parameters);
    return parameters;
  };

  // Track a specific page view
  const trackPageView = (eventName: string, parameters: JSONObject) => {
    analyticsContext.page(eventName, addSourceField(parameters));
  };

  return {
    trackEvent,
    trackPageView
  };
};
export default useAnalytics;
